<template>
  <div
    class="bg-white h-full container-with-transition"
    :style="{ minHeight: `${height + 50}px` }"
  >
    <div
      v-if="isInitialLoading"
      class="flex items-center justify-center h-[400px] bg-white"
    >
      <div class="text-center">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F95031] mx-auto mb-2" />
      </div>
    </div>

    <iframe
      v-show="!isInitialLoading"
      ref="marketplaceIntegrationIframe"
      class="iframe-with-transition"
      frameborder="1"
      :style="{ height: `${height + 50}px` }"
      width="100%"
      :src="iframeSrc"
      allow="clipboard-read; clipboard-write"
      @load="handleIframeLoad"
    />

    <router-view />

    <ModalMarketplaceList
      ref="marketplaceList"
      @next="handleNextMarketplaceList"
    />

    <ModalMappingWarehouse
      ref="modalMappingWarehouse"
      :warehouse-fetcher="subscribedWarehouseFetcher"
      @complete="handleCompleteMappingWarehouse"
    />

    <ModalConfirm ref="modalConfirm" />

    <ModalSyncProduct
      ref="modalSyncProduct"
      @success="handleSuccessProductSync"
    />

    <ModalSyncVariant
      ref="modalSyncVariant"
      @success="handleSuccessVariantSync"
    />

    <ModalWarehouseMapping
      ref="modalWarehouseMapping"
      :warehouse-fetcher="subscribedWarehouseFetcher"
      @success="handleSuccessMappingWarehouse"
    />

    <ModalExtendMarketplaceToken
      ref="modalExtendMarketplaceToken"
      @reauthenticate="handleReauthenticate"
    />
  </div>
</template>

<script>
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import ModalConfirm from './ModalConfirm.vue'
import ModalMappingWarehouse from './ModalMappingWarehouse.vue'
import ModalMarketplaceList from './ModalMarketplaceList.vue'
import { MAPPING_PATH } from './config'
import ModalSyncProduct from './products-mapping/ModalSyncProduct.vue'
import ModalSyncVariant from './products-mapping/variants-mapping/ModalSyncVariant.vue'
import ModalWarehouseMapping from './ModalWarehouseMappingV2.vue'
import ModalExtendMarketplaceToken from './ModalExtendMarketplaceToken.vue'

export default {
  components: {
    ModalMarketplaceList,
    ModalMappingWarehouse,
    ModalConfirm,
    ModalSyncProduct,
    ModalSyncVariant,
    ModalWarehouseMapping,
    ModalExtendMarketplaceToken,
  },
  data() {
    return {
      height: 400,
      iframeSrc: null,
      isInitialLoading: true,
    }
  },
  computed: {
    shopId() {
      return this.$route.params.shopId
    },
    marketplaceProductId() {
      return this.$route.params.productId
    },
  },
  watch: {
    // keep iframe synced with the browser back and forward navigation
    $route: {
      handler(newVal) {
        if (!this.$refs.marketplaceIntegrationIframe || !this.$refs.marketplaceIntegrationIframe?.contentWindow) return
        this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
          type: 'SYNC_PATH',
          data: {
            path: MAPPING_PATH[newVal.name],
            params: newVal.params,
            query: newVal.query,
          },
        }, '*')
      },
      immediate: true,
    },
  },
  created() {
    // set the iframe source based on the route (integrasi-marketplace or integrasi-marketplace-mapping-product)
    const { name } = this.$route
    const query = new URLSearchParams(this.$route.query).toString()

    if (name === 'integrasi-marketplace') {
      this.iframeSrc = `${process.env.VUE_APP_BASE_AUTH_MODULE}/kompack/marketplace-integration`
    }

    if (name === 'integrasi-marketplace-mapping-product') {
      this.iframeSrc = `${process.env.VUE_APP_BASE_AUTH_MODULE}/kompack/marketplace-integration/${this.shopId}/products-mapping${query ? `?${query}` : ''}`
    }

    if (name === 'integrasi-marketplace-mapping-variants') {
      this.iframeSrc = `${process.env.VUE_APP_BASE_AUTH_MODULE}/kompack/marketplace-integration/${this.shopId}/products-mapping/${this.marketplaceProductId}/variants-mapping${query ? `?${query}` : ''}`
    }

    this.subscribedWarehouseFetcher = null
    this.initializeFetcherWarehouses()
  },
  mounted() {
    window.addEventListener('message', this.watchIframe)
    this.initBroadcastChannel()
  },
  destroyed() {
    window.removeEventListener('message', this.watchIframe)
    this.closeBroadcastChannel()
  },
  methods: {
    /**
     * Listen to broadcast message from window popup so we can close it automatically.
     */
    initBroadcastChannel() {
      try {
        this.bc = new BroadcastChannel('marketplace_integration')
        this.bc.onmessage = event => {
          if (event.data && event.data.type === 'INTEGRATION_SUCCESS') {
            window.location.reload()
          }
        }
      } catch (e) {
        console.error('BroadcastChannel initialization error:', e)
      }
    },
    closeBroadcastChannel() {
      if (this.bc) {
        this.bc.close()
      }
    },
    /**
     * Returns the window features for the integration popup.
     */
    integrationPopupWindowFeatures() {
      if (typeof window === 'undefined') {
        return 'width=1050,height=800,menubar=no,toolbar=no,location=no,status=no'
      }

      const { width, height } = window.screen

      const popupWidth = Math.max(320, Math.min(1300, width - 32))
      const popupHeight = Math.max(480, Math.min(800, height - 32))
      const popupLeft = Math.max(0, Math.floor((width - popupWidth) / 2))
      const popupTop = Math.max(0, Math.floor((height - popupHeight) / 2))

      return `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop},menubar=no,toolbar=no,location=no,status=no`
    },
    /**
     * Menangani `postMessage` dari iframe integrasi marketplace (origin wajib `VUE_APP_BASE_AUTH_MODULE`).
     *
     * @param {MessageEvent} event
     */
    async watchIframe(event) {
      if (event.origin !== process.env.VUE_APP_BASE_AUTH_MODULE) return

      const {
        type,
        data,
      } = event.data

      if (type === 'INTEGRATION_WINDOW_REQUESTED') {
        const newWindow = window.open(data.auth_url, '_blank', this.integrationPopupWindowFeatures())
        if (newWindow) {
          newWindow.focus()
        }
      }

      if (type === 'STORES_WITH_EXPIRED_TOKEN') {
        this.$refs.modalExtendMarketplaceToken.show(data)
        return
      }

      if (type === 'WAREHOUSE_MAPPING') {
        this.$refs.modalWarehouseMapping.show(data)
        return
      }

      if (type === 'REFRESH_SHOP_STATUS') {
        if (data.success) {
          this.$toast_success({
            message: 'Refresh toko berhasil',
          })

          return
        }

        this.$toast_error({
          message: 'Refresh toko gagal, silahkan coba lagi',
        })

        return
      }

      if (type === 'SYNC_VARIANT') {
        this.$refs.modalSyncVariant.setData(data)

        this.$refs.modalSyncVariant.showModal()

        return
      }

      if (type === 'SYNC_PRODUCT') {
        this.$refs.modalSyncProduct.setData(data)

        this.$refs.modalSyncProduct.show()

        return
      }

      if (type === 'SYNC_FILTER') {
        const currentQuery = { ...this.$route.query, ...data.query }

        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params,
          query: currentQuery,
        })

        return
      }

      if (type === 'GO_TO' && data.destination === 'mi:pm') {
        this.$router.push({
          name: 'integrasi-marketplace-mapping-product',
          params: { shopId: data.shop_id },
          query: { marketplace: data.marketplace, mai: data.access_id },
          replace: true,
        })

        return
      }

      if (type === 'GO_TO' && data.destination === 'mi:vm') {
        this.$router.push({
          name: 'integrasi-marketplace-mapping-variants',
          params: { shopId: data.shop_id, productId: data.marketplace_product_id },
          query: { ...data.query },
          replace: true,
        })

        return
      }

      if (type === 'GO_BACK') {
        this.$router.go(-1)

        return
      }

      if (type === 'TOGGLE_ACTIVE_STORE') {
        this.handleToggleActiveStoreEvent(data)

        return
      }

      if (type === 'OPEN_ADD_WAREHOUSE_POPUP') {
        if (this.subscribedWarehouseFetcher.data.length === 0) {
          await this.subscribedWarehouseFetcher.fetchData({ reset: true })
        }
        if (
          !this.subscribedWarehouseFetcher?.data.length
          && !this.subscribedWarehouseFetcher?.isLoading
          && !this.subscribedWarehouseFetcher?.error
        ) {
          this.$refs.modalConfirm.show({
            status: 'WARNING',
            title: 'Kamu Belum Memiliki Gudang',
            description: 'Silakan subscribe gudang terlebih dahulu agar dapat melakukan mapping gudang di menu Integrasi Marketplace.',
            cancelLabel: 'Batal',
            confirmLabel: 'Cari Gudang',
            onCancel: () => {
              this.$refs.modalConfirm.hide()
            },
            onConfirm: () => {
              this.$router.push({ name: 'search-gudang' })
              this.$refs.modalConfirm.hide()
            },
          })

          return
        }

        this.$refs.modalMappingWarehouse.show(data)

        return
      }

      if (type === 'SHOW_INTEGRATION_POPUP') {
        this.$refs.marketplaceList.show()

        return
      }

      if (type === 'INTEGRATION_PROCESS_COMPLETE') {
        window.location.reload()

        return
      }

      if (type === 'SET_DIMENSION' && data.height > 400) {
        this.height = Math.floor(data.height)
      }
    },
    /**
     * Handle the iframe load event
     */
    handleIframeLoad() {
      this.isInitialLoading = false
    },
    /**
     * Handle the next marketplace list event from the child component.
     * @param {Object} selectedMarketplace - the selected marketplace
     */
    async handleNextMarketplaceList(selectedMarketplace) {
      this.$refs.marketplaceList.setOnProgress({ isOnProgress: true })
      if (this.$refs.marketplaceIntegrationIframe) {
        this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
          type: 'OPEN_INTEGRATION_WINDOW',
          data: {
            marketplace: selectedMarketplace,
          },
        }, '*')
      }

      try {
        await this.waitForOpenIntegrationWindowStatus(this.$refs.marketplaceIntegrationIframe.contentWindow)

        this.$refs.marketplaceList.hide()
      } catch (error) {
        this.$toast_error({
          message: 'Gagal melakukan integrasi marketplace, silahkan coba lagi',
        })
      } finally {
        this.$refs.marketplaceList.setOnProgress({ isOnProgress: false })
      }
    },
    /**
     * Handle routing user back to the previous page
     */
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push({ name: 'search-gudang' })
      }
    },
    /**
     * Handle the complete mapping warehouse event from the child component.
     * @param {Object} data - warehouse and address data
     */
    handleCompleteMappingWarehouse({
      marketplace,
      warehouseId,
      addressId,
      shopId,
    }) {
      const processFn = async () => {
        try {
          await this.$http_new.post('/komship/api/v1/marketplace-shops/warehouses', {
            warehouse_id: warehouseId,
            address_id: String(addressId),
            shop_id: shopId,
          })

          // wait for 1 second to simulate the loading process, so it can't be flickering
          await new Promise(resolve => setTimeout(resolve, 1000))

          this.$refs.modalConfirm.show({
            status: 'SUCCESS',
            title: 'Mapping Gudang Berhasil',
            description: 'Toko kamu di marketplace berhasil terhubung dengan Gudang Kompack. Selanjutnya, sinkronkan SKU produk marketplace dengan SKU di gudang kamu.',
            confirmLabel: 'Mapping Produk',
            onConfirm: () => {
              this.$router.push({
                name: 'integrasi-marketplace-mapping-product',
                params: { shopId },
                query: { marketplace },
              })
              this.$refs.modalConfirm.hide()
              this.$refs.modalMappingWarehouse.hide()
              this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
                type: 'SUCCESS_MAPPING_WAREHOUSE',
              }, '*')
            },
            cancelLabel: 'Nanti',
            onCancel: () => {
              this.$refs.modalConfirm.hide()
              this.$refs.modalMappingWarehouse.hide()
              this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
                type: 'SUCCESS_MAPPING_WAREHOUSE',
              }, '*')
            },
          })
        } catch (error) {
          this.$refs.modalConfirm.show({
            status: 'WARNING',
            title: 'Mapping Gudang Gagal',
            description: 'Silakan coba lagi atau hubungi admin Komerce untuk bantuan.',
            cancelLabel: 'Batal',
            confirmLabel: 'Coba Lagi',
            onConfirm: () => {
              this.$refs.modalConfirm.show({
                status: 'WARNING',
                title: 'Loading Proses Mapping',
                description: 'Proses mapping gudang Kompack dengan toko kamu sedang berlangsung, Mohon ditunggu ya',
                processFn,
              })
            },
            onCancel: () => {
              this.$refs.modalConfirm.hide()
              this.$refs.modalMappingWarehouse.hide()
            },
          })
        } finally {
          this.isMutating = false
        }
      }

      this.$refs.modalConfirm.show({
        status: 'WARNING',
        title: 'Loading Proses Mapping',
        description: 'Proses mapping gudang Kompack dengan toko kamu sedang berlangsung, Mohon ditunggu ya',
        processFn,
      })
    },
    /**
     * setup the fetcher for the subscribed warehouses
     */
    initializeFetcherWarehouses() {
      const fnFetch = ({
        offset, limit,
      }) => this.$http_new.get('/komship/api/v1/subscribe-warehouses/products', {
        params: {
          offset,
          limit,
        },
      }).then(res => {
        const { data } = res.data
        if (!data) {
          return {
            data: [],
          }
        }

        return {
          data,
        }
      }).catch(() => {
        this.$toast_error({
          message: 'Gagal mengambil data gudang, silahkan coba lagi',
        })

        throw new Error('Gagal mengambil data gudang, silahkan coba lagi')
      })

      this.subscribedWarehouseFetcher = createPaginatedFetcher(fnFetch, 0, 50)
    },
    /**
     * Handle the toggle active store event from the iframe
     * @param {Object} data - the data to toggle active store
     * @param {Object} data.marketplace - the marketplace data
     * @param {Boolean} data.is_active - the active status of the store
     */
    async handleToggleActiveStoreEvent(data) {
      try {
        if (!data.is_active) {
          this.$refs.modalConfirm.show({
            status: 'WARNING',
            title: 'Yakin Akan Mengaktifkan Toko?',
            description: 'Sebelum mengaktifkan toko, Anda harus login ke platform marketplace. Setelah toko aktif, semua layanan penjualan akan kembali tersedia.',
            cancelLabel: 'Batal',
            confirmLabel: 'Login',
            onCancel: () => {
              this.$refs.modalConfirm.hide()
            },
            onConfirm: async () => {
              const iframeWindow = this.$refs.marketplaceIntegrationIframe?.contentWindow
              if (!iframeWindow) return
              iframeWindow.postMessage({
                type: 'OPEN_INTEGRATION_WINDOW',
                data: {
                  marketplace: data.marketplace,
                },
              }, '*')

              try {
                await this.waitForOpenIntegrationWindowStatus(iframeWindow)
              } catch (error) {
                this.$toast_error({
                  message: 'Gagal mengaktifkan toko, silahkan coba lagi',
                })
              }
            },
          })

          return
        }

        this.$refs.modalConfirm.show({
          status: 'WARNING',
          title: 'Yakin Akan Menonaktifkan Toko?',
          description: 'Jika toko dinonaktifkan, semua layanan penjualan yang tersedia di toko ini akan menjadi tidak aktif.',
          cancelLabel: 'Batal',
          confirmLabel: 'Yakin',
          onCancel: () => {
            this.$refs.modalConfirm.hide()
          },
          onConfirm: async () => {
            await this.handleOnConfirmToggleActiveStore({ shopId: data.shop_id })
          },
        })
      } catch (error) {
        this.$toast_error({
          message: 'Gagal mengubah status toko, silahkan coba lagi',
        })
      }
    },
    /**
     * Handle the on confirm toggle active store when confirm button is clicked
     */
    async handleOnConfirmToggleActiveStore({ shopId }) {
      try {
        await this.$http_new.patch(`/komship/api/v1/marketplace-shops/${shopId}/deactivate`)

        this.$refs.modalConfirm.show({
          status: 'SUCCESS',
          title: 'Toko Berhasil Dinonaktifkan',
          description: 'Toko telah dinonaktifkan, semua layanan penjualan di toko ini tidak aktif. Jika Anda ingin mengaktifkan toko, silakan login kembali',
          confirmLabel: 'Selesai',
          onConfirm: () => {
            this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
              type: 'REFETCH_LIST_MARKETPLACE_SHOPS',
            }, '*')
            this.$refs.modalConfirm.hide()
          },
        })
      } catch (error) {
        this.$toast_error({
          message: 'Gagal menonaktifkan toko, silahkan coba lagi',
        })
      }
    },
    /**
     * Wait for the next postMessage event from the integration iframe
     * @param {Window} iframeWindow - the iframe window reference
     * @returns {Promise<any>} - resolves with the message data
     */
    waitForOpenIntegrationWindowStatus(iframeWindow) {
      return new Promise((resolve, reject) => {
        const handler = event => {
          if (
            event.source !== iframeWindow
            || event.data.type !== 'OPEN_INTEGRATION_WINDOW_STATUS'
          ) return

          if (event.data.data.status === 'SUCCESS') {
            resolve()
          } else {
            reject(new Error('Gagal mengaktifkan toko, silahkan coba lagi'))
          }

          window.removeEventListener('message', handler)
          resolve(event.data)
        }

        window.addEventListener('message', handler)
      })
    },
    handleSuccessProductSync(data) {
      this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
        type: data.type === 'sync' ? 'PRODUCT_SYNCED' : 'PRODUCT_UNSYNCED',
        data,
      }, '*')

      this.$refs.modalSyncProduct.closeModal()
    },
    handleSuccessVariantSync(data) {
      this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
        type: data.type === 'sync' ? 'VARIANT_SYNCED' : 'VARIANT_UNSYNCED',
        data,
      }, '*')

      this.$refs.modalSyncVariant.closeModal()
    },
    /**
     * Meneruskan hasil simpan mapping gudang ke iframe (`SUCCESS_MAPPING_WAREHOUSE`).
     * @param {Array<{ ok: boolean, error?: string }>} results - Satu entri per request; `ok: false` berarti gagal untuk baris tersebut.
     */
    handleSuccessMappingWarehouse(results) {
      this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
        type: 'SUCCESS_MAPPING_WAREHOUSE',
        data: results,
      }, '*')
    },
    /**
     * Menangani event `reauthenticate` dari ModalExtendMarketplaceToken.
     * Membuka popup integrasi marketplace dan menunggu hasilnya.
     * @param {Object} params
     * @param {string} params.marketplace - nama marketplace yang akan direauthentikasi
     */
    async handleReauthenticate({ marketplace, statusCb }) {
      if (this.$refs.marketplaceIntegrationIframe) {
        this.$refs.marketplaceIntegrationIframe.contentWindow.postMessage({
          type: 'OPEN_INTEGRATION_WINDOW',
          data: {
            marketplace,
          },
        }, '*')
      }

      try {
        await this.waitForOpenIntegrationWindowStatus(this.$refs.marketplaceIntegrationIframe.contentWindow)
        statusCb(true)
      } catch (error) {
        this.$toast_error({
          message: 'Gagal melakukan reautentikasi marketplace, silahkan coba lagi',
        })
        statusCb(false)
      }
    },
  },
}
</script>

<style scoped>
.container-with-transition {
  transition: min-height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.iframe-with-transition {
  transition: height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

@media (prefers-reduced-motion: reduce) {

  .container-with-transition,
  .iframe-with-transition {
    transition: none;
  }
}
</style>
